<script setup lang="ts">
import { t } from '@/i18n';

/*
 * "This stopped updating — here is the way back."
 *
 * The polling feed gives up after an idle hour rather than waking a phone in a pocket all
 * afternoon, and this is the only thing on screen that says so. Without it a stale grid is
 * indistinguishable from a quiet one.
 *
 * **It was three components.** `GameView` and `HostGameView` had it byte for byte, comment
 * included; `LocalPlayView` had a third variant that had drifted — no `role="status"`, so a screen
 * reader was never told, and no `:disabled`, so the button could be pressed again mid-refresh.
 * Bringing that one into line is most of the reason this file exists.
 *
 * `role="status"` and not `alert`: this is worth hearing at the next pause, not worth interrupting
 * for.
 */
const props = defineProps<{
  /** Whether the feed has given up. */
  idle: boolean;
  /** Whether a refresh is already in flight, so the button cannot be pressed twice. */
  refreshing: boolean;
  /**
   * The i18n keys to use. Local play says "the board" where the shared screens say "this game",
   * which is the only thing that ever legitimately differed between the three.
   */
  messageKey?: string;
  actionKey?: string;
}>();

const emit = defineEmits<{ refresh: [] }>();

const message = () => t(props.messageKey ?? 'game.idle');
const action = () => t(props.actionKey ?? 'game.showLatest');
</script>

<template>
  <div
    v-if="idle"
    role="status"
    class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface p-3"
  >
    <p class="text-sm">{{ message() }}</p>
    <button
      type="button"
      :disabled="refreshing"
      class="rounded bg-accent px-4 py-2 text-sm font-medium text-on-accent hover:bg-accent-strong disabled:border-line disabled:bg-surface-sunken disabled:text-ink-muted"
      @click="emit('refresh')"
    >
      {{ action() }}
    </button>
  </div>
</template>
